import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { clsx } from "@/utils";
import { Icon } from "..";

export interface DropdownCheckbox extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  selectedCount?: number;
  hasError?: boolean;
  disabled?: boolean;
  getSelectedLabel?: (selected?: number) => string;
  onClick?: () => void;
  className?: string;
  /** Open the panel above the trigger (e.g. low in a modal, like DropdownSelect). */
  position?: "bottom" | "top";
}

export const DropdownCheckbox = ({
  placeholder = "Select",
  selectedCount,
  getSelectedLabel,
  hasError = false,
  disabled = false,
  className,
  children,
  onClick,
  position = "bottom",
  ...props
}: DropdownCheckbox) => {
  const errorClass = {
    "!border-danger": hasError,
  };

  const label = getSelectedLabel ? getSelectedLabel(selectedCount) : `${selectedCount} Selected`;

  return (
    <Popover className={clsx("relative", className, errorClass)} {...props}>
      {() => (
        <>
          <PopoverButton
            disabled={disabled}
            className={clsx(
              "group min-h-[48px] w-full rounded-md border border-neutral-mid-gray py-2 pl-3 pr-10 text-left shadow-sm focus:outline-0 sm:text-base",
              disabled ? "cursor-not-allowed bg-neutral-surface-gray" : "cursor-pointer bg-white"
            )}
            onClick={onClick}
          >
            {selectedCount ? (
              // Disabled matches DropdownSelect: mid-gray label + arrow on a surface-gray fill.
              <span className={clsx("block truncate font-medium", disabled && "text-neutral-mid-gray")}>{label}</span>
            ) : (
              <span className="block truncate font-medium text-neutral-mid-gray">{placeholder}</span>
            )}
            <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
              <Icon name="arrow-down" isCustom className={clsx("h-5 w-5", disabled ? "text-neutral-mid-gray" : "text-black")} />
            </span>
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx("dropdown-panel absolute left-1/2 z-10 w-full -translate-x-1/2 transform sm:px-0", {
                "mt-3": position === "bottom",
                "bottom-full mb-3": position === "top",
              })}
            >
              {/* Bounded + internally scrollable (same containment as DropdownSelect)
                  so a long list never grows past the modal/page. */}
              <div className="flex max-h-72 flex-col gap-6 overflow-y-auto rounded bg-white p-5 shadow-md ring-1 ring-neutral-gray empty:hidden">
                {children}
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
